import {
  driverGraphQLAPIToDriver,
  driverGraphQLAPIToDriverNames,
  driversImportGraphqlResponse,
  driverToSaveDriverGraphQLAPI,
  newDriversToAddDriverGraphQLAPI,
  newDriverToAddDriverGraphQLAPI,
} from './APITransformer';
import type { DriverGraphQLAPI, DriverSpreadsheetResultAPI, IDriverSpreadsheetRowAPI } from './APITypes';
import type { Driver, DriverSpreadsheetResult, NewDriver } from './Types';
import { gql } from '@apollo/client';

import type { DriverNames, UUID } from '@cobliteam/common-types';

import graphQLRequester from '../../request/GraphQLRequester';

export class GraphQLDriversRequester {
  public getDrivers = (): Promise<Driver[]> =>
    graphQLRequester
      .query<{ drivers: DriverGraphQLAPI[] }>({
        query: gql`
          query drivers {
            drivers {
              id
              name
              fleet_id
              assigned
              phone_number
              first_license_acquirement_date
              license_expiration_date
              license_category
              driver_code
              identifier
              cpf
              driver_license
              registry_id
            }
          }
        `,
      })
      .then((result) => result.drivers.map(driverGraphQLAPIToDriver));

  public getDriversWithVehicles = (): Promise<Driver[]> =>
    graphQLRequester
      .query<{ drivers: DriverGraphQLAPI[] }>({
        query: gql`
          query drivers {
            drivers {
              id
              name
              fleet_id
              assigned
              phone_number
              first_license_acquirement_date
              license_expiration_date
              license_category
              driver_code
              identifier
              cpf
              driver_license
              registry_id
              vehicle {
                licensePlate
              }
            }
          }
        `,
      })
      .then((result) => result.drivers.map(driverGraphQLAPIToDriver));

  public getDriverNames = (): Promise<DriverNames> =>
    graphQLRequester
      .query<{ drivers: DriverGraphQLAPI[] }>({
        query: gql`
          query drivers {
            drivers {
              id
              name
            }
          }
        `,
      })
      .then((result) => driverGraphQLAPIToDriverNames(result.drivers));

  public deleteDriver = (driverId: UUID): Promise<UUID> => {
    const driverToDelete = { id: driverId };
    return graphQLRequester
      .mutate({
        mutation: gql`
          mutation DeleteDriver($driverToDelete: DeleteDriver) {
            deleteDriver(input: $driverToDelete) {
              id
            }
          }
        `,
        variables: { driverToDelete },
      })
      .then(() => driverToDelete.id);
  };

  public saveDriver = (driver: Driver | NewDriver): Promise<Driver> => {
    if ('id' in driver && driver.id) {
      return graphQLRequester
        .mutate<{ updateDriver: DriverGraphQLAPI }>({
          mutation: gql`
            mutation UpdateDriver($driverToUpdate: UpdateDriver) {
              updateDriver(input: $driverToUpdate) {
                id
                name
                fleet_id
                assigned
                phone_number
                first_license_acquirement_date
                license_expiration_date
                license_category
                driver_code
                cpf
                driver_license
                registry_id
              }
            }
          `,
          variables: {
            driverToUpdate: driverToSaveDriverGraphQLAPI(driver),
          },
        })
        .then((graphQlResponse) => driverGraphQLAPIToDriver(graphQlResponse.updateDriver));
    }
    return graphQLRequester
      .mutate<{ addDriver: DriverGraphQLAPI }>({
        mutation: gql`
          mutation AddDriver($driverToAdd: AddDriver) {
            addDriver(input: $driverToAdd) {
              id
              name
              fleet_id
              assigned
              phone_number
              first_license_acquirement_date
              license_expiration_date
              license_category
              driver_code
            }
          }
        `,
        variables: {
          driverToAdd: newDriverToAddDriverGraphQLAPI(driver),
        },
      })
      .then((graphQlResponse) => driverGraphQLAPIToDriver(graphQlResponse.addDriver));
  };

  public saveDrivers = (drivers: IDriverSpreadsheetRowAPI[]): Promise<Driver[]> => {
    return graphQLRequester
      .mutate<{ addDrivers: DriverGraphQLAPI[] }>({
        mutation: gql`
          mutation AddDrivers($driversToCreate: [AddDriver]) {
            addDrivers(input: $driversToCreate) {
              id
              name
            }
          }
        `,
        variables: {
          driversToCreate: drivers.map(newDriversToAddDriverGraphQLAPI),
        },
      })
      .then((graphQlResponse) => graphQlResponse.addDrivers.map(driverGraphQLAPIToDriver));
  };

  public driversImport = (drivers: IDriverSpreadsheetRowAPI[]): Promise<DriverSpreadsheetResult> => {
    return graphQLRequester
      .mutate<{ importDriverSpreadsheet: DriverSpreadsheetResultAPI }>({
        mutation: gql`
          mutation importDriverSpreadsheet(
            $driversToImport: [DriverSpreadsheetRow]
          ) {
            importDriverSpreadsheet(input: $driversToImport) {
              valid_rows {
                name
                phone_number
                first_license_acquirement_date
                license_expiration_date
                license_category
              }
              invalid_rows {
                data {
                  name
                  phone_number
                  first_license_acquirement_date
                  license_expiration_date
                  license_category
                }
                errors
                index
              }
            }
          }
        `,
        variables: {
          driversToImport: drivers.map(newDriversToAddDriverGraphQLAPI),
        },
      })
      .then((graphQlResponse) => driversImportGraphqlResponse(graphQlResponse.importDriverSpreadsheet));
  };
}

export default GraphQLDriversRequester;
